import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import TopNav from './TopNav';
import { useToast } from '../Toast';
import { compressImage } from '../imageCompress';

const API = process.env.REACT_APP_API_URL || '';

// Recount photo thumbnail (auth-fetch → blob → object URL); bust re-fetches after replace.
function SpiritsPhotoThumb({ location, weekStart, itemId, bust }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let revoked = false, obj = null;
    fetch(`${API}/api/spirits/counts/photo?location=${location}&week_start=${weekStart}&item_id=${itemId}`, { credentials: 'include' })
      .then(r => (r.ok ? r.blob() : null))
      .then(b => { if (b && !revoked) { obj = URL.createObjectURL(b); setUrl(obj); } })
      .catch(() => {});
    return () => { revoked = true; if (obj) URL.revokeObjectURL(obj); };
  }, [location, weekStart, itemId, bust]);
  if (!url) return null;
  return (
    <a href={url} target="_blank" rel="noreferrer">
      <img src={url} alt="Count" className="h-9 w-9 object-cover rounded border border-gray-700 inline-block align-middle" />
    </a>
  );
}

export const LOCATIONS = [
  { key: 'midtown',    label: 'Midtown',    color: '#F05A28' },
  { key: 'northside',  label: 'Northside',  color: '#22c55e' },
  { key: 'power_mill', label: 'Power Mill', color: '#3b82f6' },
  { key: 'tampa',      label: 'Tampa',      color: '#a855f7' },
];

export function isoWeekStart(d = new Date()) {
  const date = new Date(d);
  date.setHours(0, 0, 0, 0);
  const dow = date.getDay();
  const offset = dow === 0 ? -6 : 1 - dow;
  date.setDate(date.getDate() + offset);
  return date.toLocaleDateString('en-CA');
}

function fmtQty(n) {
  const v = parseFloat(n);
  if (!isFinite(v)) return '0';
  return Number.isInteger(v) ? String(v) : v.toFixed(2);
}

function fmtWeekRange(weekStart) {
  if (!weekStart) return '';
  const [y, m, d] = weekStart.split('-').map(Number);
  const start = new Date(y, m - 1, d);
  const end = new Date(start);
  end.setDate(end.getDate() + 6);
  const opts = { month: 'short', day: 'numeric' };
  return `${start.toLocaleDateString('en-US', opts)} – ${end.toLocaleDateString('en-US', opts)}, ${y}`;
}

function fmtDateTime(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit', hour12: true,
  });
}

// ── Inline count input that saves on blur ────────────────────────────────────

function CountCell({ value, onSave, disabled, step = 1, allowDecimal = false }) {
  const [val, setVal] = useState(value == null ? '' : String(value));
  const [saving, setSaving] = useState(false);
  const initial = useRef(value == null ? '' : String(value));

  useEffect(() => {
    if (!saving) {
      const incoming = value == null ? '' : String(value);
      setVal(incoming);
      initial.current = incoming;
    }
  }, [value, saving]);

  const commit = async () => {
    if (val === initial.current) return;
    if (val === '' || isNaN(parseFloat(val))) {
      setVal(initial.current);
      return;
    }
    setSaving(true);
    try {
      const parsed = allowDecimal ? parseFloat(val) : parseInt(val, 10);
      await onSave(parsed);
      initial.current = String(parsed);
      setVal(String(parsed));
    } finally {
      setSaving(false);
    }
  };

  return (
    <input
      type="number" min="0" step={step} inputMode={allowDecimal ? 'decimal' : 'numeric'}
      value={val}
      disabled={disabled}
      onChange={e => setVal(e.target.value)}
      onBlur={commit}
      onKeyDown={e => { if (e.key === 'Enter') e.currentTarget.blur(); }}
      className="w-20 bg-gray-700 border border-gray-600 rounded-lg px-2 py-1.5 text-white text-sm text-right focus:outline-none focus:ring-2 focus:ring-orange-500 disabled:opacity-50"
    />
  );
}

// ── Mobile wheel count picker ─────────────────────────────────────────────────
// Taps open an iOS-style scroll wheel bottom sheet instead of a fiddly number
// keyboard. Integers only (bottle counts). Used only on mobile; the desktop
// table keeps the plain CountCell input.

const WHEEL_ITEM_H = 44;   // px per row
const WHEEL_VISIBLE = 5;   // rows shown (odd, so one is centered)

function WheelSheet({ initial, min, max, title, subtitle, onCancel, onDone }) {
  const scrollRef = useRef(null);
  const [selected, setSelected] = useState(initial);
  const [text, setText] = useState(String(initial));
  const lastVibrated = useRef(initial);

  const values = useMemo(() => {
    const arr = [];
    for (let n = min; n <= max; n++) arr.push(n);
    return arr;
  }, [min, max]);

  // Center the wheel on the starting value, and lock body scroll while open.
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = (initial - min) * WHEEL_ITEM_H;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [initial, min]);

  const handleScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    const idx = Math.round(el.scrollTop / WHEEL_ITEM_H);
    const v = Math.min(max, Math.max(min, min + idx));
    if (v !== selected) { setSelected(v); setText(String(v)); }
    if (v !== lastVibrated.current) {
      lastVibrated.current = v;
      if (navigator.vibrate) navigator.vibrate(4); // subtle tick per notch
    }
  };

  // Typed entry — kept in sync with the wheel. We only reposition the wheel on
  // blur (not per keystroke) to avoid fighting the scroll handler mid-type.
  const handleTextChange = (e) => {
    const t = e.target.value.replace(/[^\d]/g, '');
    setText(t);
    if (t !== '') setSelected(Math.min(max, Math.max(min, parseInt(t, 10))));
  };
  const handleTextBlur = () => {
    const n = text === '' ? min : Math.min(max, Math.max(min, parseInt(text, 10) || min));
    setSelected(n);
    setText(String(n));
    if (scrollRef.current) scrollRef.current.scrollTop = (n - min) * WHEEL_ITEM_H;
  };

  const jumpTo = (n) => {
    scrollRef.current?.scrollTo({ top: (n - min) * WHEEL_ITEM_H, behavior: 'smooth' });
  };

  const pad = ((WHEEL_VISIBLE - 1) / 2) * WHEEL_ITEM_H;

  return (
    <div className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center">
      <div className="absolute inset-0 bg-black/60" onClick={() => onDone(selected)} />
      <div className="relative w-full sm:max-w-sm bg-gray-800 border-t sm:border border-gray-700 rounded-t-2xl sm:rounded-2xl p-4 pb-6 animate-[fadeIn_0.15s_ease-out]">
        <div className="text-center mb-3">
          {subtitle && <p className="text-gray-400 text-xs truncate">{subtitle}</p>}
          <p className="text-cream font-semibold mb-2">{title}</p>
          <input
            type="text" inputMode="numeric" pattern="[0-9]*"
            value={text}
            onChange={handleTextChange}
            onBlur={handleTextBlur}
            onFocus={e => e.target.select()}
            onKeyDown={e => { if (e.key === 'Enter') { e.currentTarget.blur(); onDone(selected); } }}
            aria-label={`${title} — type a number`}
            className="w-24 mx-auto block text-center bg-gray-900 border border-gray-600 rounded-lg py-1.5 text-white text-xl font-bold tabular-nums focus:outline-none focus:ring-2 focus:ring-orange-500"
          />
          <p className="text-gray-500 text-[10px] mt-1">type or spin</p>
        </div>

        <div className="relative mx-auto max-w-[220px]" style={{ height: WHEEL_VISIBLE * WHEEL_ITEM_H }}>
          {/* Fixed center band marking the selected row */}
          <div className="pointer-events-none absolute inset-x-0 top-1/2 -translate-y-1/2 rounded-lg bg-gray-700/40 border-y border-gray-600"
            style={{ height: WHEEL_ITEM_H }} />
          {/* Top/bottom fade for depth */}
          <div className="pointer-events-none absolute inset-x-0 top-0 z-10 bg-gradient-to-b from-gray-800 to-transparent" style={{ height: pad }} />
          <div className="pointer-events-none absolute inset-x-0 bottom-0 z-10 bg-gradient-to-t from-gray-800 to-transparent" style={{ height: pad }} />

          <div
            ref={scrollRef}
            onScroll={handleScroll}
            className="no-scrollbar h-full overflow-y-scroll"
            style={{ scrollSnapType: 'y mandatory', WebkitOverflowScrolling: 'touch' }}
          >
            <div style={{ height: pad }} />
            {values.map(n => (
              <div
                key={n}
                onClick={() => jumpTo(n)}
                className={`flex items-center justify-center tabular-nums transition-all duration-100 ${
                  n === selected ? 'text-white text-3xl font-bold' : 'text-gray-500 text-xl'
                }`}
                style={{ height: WHEEL_ITEM_H, scrollSnapAlign: 'center' }}
              >
                {n}
              </div>
            ))}
            <div style={{ height: pad }} />
          </div>
        </div>

        <div className="flex gap-2 mt-4">
          <button onClick={onCancel} className="flex-1 py-2.5 bg-gray-700 text-white text-sm rounded-lg hover:bg-gray-600 transition">Cancel</button>
          <button onClick={() => onDone(selected)} className="flex-1 py-2.5 text-white text-sm rounded-lg font-semibold transition" style={{ backgroundColor: '#F05A28' }}>Done</button>
        </div>
      </div>
    </div>
  );
}

function WheelCountCell({ value, onSave, disabled, label, itemName }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const current = value == null ? null : (parseInt(value, 10) || 0);
  // Give the wheel/typed entry generous headroom above the highest count.
  const max = Math.max(300, (current || 0) + 50);

  const commit = async (n) => {
    setOpen(false);
    if (value != null && n === current) return; // unchanged
    setSaving(true);
    try { await onSave(n); } finally { setSaving(false); }
  };

  return (
    <>
      <button
        type="button"
        disabled={disabled || saving}
        onClick={() => { if (navigator.vibrate) navigator.vibrate(8); setOpen(true); }}
        className={`w-full flex items-center justify-end gap-1.5 rounded-lg border px-3 py-2 text-lg font-semibold tabular-nums transition disabled:opacity-50 ${
          current == null
            ? 'bg-gray-700 border-gray-600 text-gray-500'
            : 'bg-gray-700 border-gray-500 text-white'
        }`}
      >
        {current == null ? '–' : current}
        <svg className="w-3.5 h-3.5 text-gray-500" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M7 8l3-3 3 3M7 12l3 3 3-3" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      {open && (
        <WheelSheet
          initial={current ?? 0}
          min={0}
          max={max}
          title={label}
          subtitle={itemName}
          onCancel={() => setOpen(false)}
          onDone={commit}
        />
      )}
    </>
  );
}

// ── Location landing page ─────────────────────────────────────────────────────

export function LocationLanding({ onSelect, lastSync }) {
  return (
    <main className="max-w-5xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
      <div className="text-center mb-8">
        <h2 className="text-cream text-3xl sm:text-4xl font-bold">Spirits Ordering</h2>
        <p className="text-gray-400 mt-2 text-sm">Pick a location to take this week's count</p>
        {lastSync && (
          <p className="text-gray-500 text-xs mt-2">Distillery inventory last synced: {fmtDateTime(lastSync)}</p>
        )}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {LOCATIONS.map(loc => (
          <button key={loc.key} onClick={() => onSelect(loc.key)}
            className="bg-gray-800 border border-gray-700 hover:border-gray-500 rounded-2xl p-6 text-left transition group">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full flex items-center justify-center text-white text-xl font-bold flex-shrink-0"
                style={{ backgroundColor: loc.color }}>
                {loc.label[0]}
              </div>
              <div className="flex-1">
                <h3 className="text-white font-semibold text-lg">{loc.label}</h3>
                <p className="text-gray-400 text-sm">Weekly count & order</p>
              </div>
              <span className="text-gray-500 group-hover:text-white text-2xl transition">→</span>
            </div>
          </button>
        ))}
      </div>
    </main>
  );
}

// ── Extra Qty editor (managers only) ──────────────────────────────────────────
// Adds bottles on top of the auto (par − bottles) recommendation.
// Blank = 0. Non-zero persists per-week and rolls into the order email.

function ExtraQtyCell({ value, onSave }) {
  const isSet = value != null && value > 0;
  const [val, setVal] = useState(isSet ? String(value) : '');
  const initial = useRef(isSet ? String(value) : '');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!saving) {
      const incoming = value != null && value > 0 ? String(value) : '';
      setVal(incoming);
      initial.current = incoming;
    }
  }, [value, saving]);

  const commit = async () => {
    if (val === initial.current) return;
    if (val !== '' && (isNaN(parseFloat(val)) || parseFloat(val) < 0)) {
      setVal(initial.current);
      return;
    }
    setSaving(true);
    try {
      await onSave(val === '' ? null : parseFloat(val));
      initial.current = val;
    } finally {
      setSaving(false);
    }
  };

  return (
    <input
      type="number" min="0" step="1" inputMode="numeric"
      value={val}
      placeholder="0"
      onChange={e => setVal(e.target.value)}
      onBlur={commit}
      onKeyDown={e => { if (e.key === 'Enter') e.currentTarget.blur(); if (e.key === 'Escape') { setVal(initial.current); e.currentTarget.blur(); } }}
      className={`w-20 bg-gray-700 border rounded-lg px-2 py-1.5 text-sm text-right focus:outline-none focus:ring-2 focus:ring-orange-500 ${
        isSet ? 'border-orange-500/60 text-orange-400 font-semibold' : 'border-gray-600 text-white placeholder-gray-500'
      }`}
    />
  );
}

// ── Inventory (count entry) tab ───────────────────────────────────────────────

export function InventoryTab({ location, items, counts, extras, pars, weekStart, canUpload, onCountSaved, onExtraSaved, countsEditable = true, showDiscrepancy = true, extrasEditable = true }) {
  const [search, setSearch] = useState('');
  const filtered = items.filter(i =>
    !search ||
    i.name.toLowerCase().includes(search.toLowerCase())
  );

  const total = items.filter(i => !i.hidden).length;
  const counted = counts.length;
  const isComplete = total > 0 && counted >= total;

  const handleSaveArea = async (itemId, area, qty) => {
    const r = await fetch(`${API}/api/spirits/counts`, {
      method: 'PATCH', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ item_id: itemId, location, week_start: weekStart, area, count_qty: qty }),
    });
    if (r.ok) onCountSaved(itemId, area, qty);
  };

  const handleSaveExtra = async (itemId, extra) => {
    const r = await fetch(`${API}/api/spirits/order-extra`, {
      method: 'PATCH', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ item_id: itemId, location, week_start: weekStart, extra_qty: extra }),
    });
    if (r.ok) onExtraSaved(itemId, extra);
  };

  const countById = new Map(counts.map(c => [c.item_id, c]));
  const extraById = new Map(extras.map(e => [e.item_id, parseFloat(e.extra_qty)]));
  const parById = new Map(pars.map(p => [p.item_id, parseFloat(p.par_level)]));

  const toast = useToast();
  // Discrepancy ceilings/floors — fetched here so the parent components need no changes.
  const [disc, setDisc] = useState({});
  useEffect(() => {
    if (!showDiscrepancy) return;
    let ok = true;
    fetch(`${API}/api/spirits/discrepancy?location=${location}&week_start=${weekStart}`, { credentials: 'include' })
      .then(r => (r.ok ? r.json() : null))
      .then(d => { if (ok && Array.isArray(d)) { const m = {}; d.forEach(x => { m[x.item_id] = x; }); setDisc(m); } })
      .catch(() => {});
    return () => { ok = false; };
  }, [location, weekStart, showDiscrepancy]);

  // Items that already have a recount photo (seeded from counts, updated on upload).
  const [photoMap, setPhotoMap] = useState({});
  useEffect(() => {
    setPhotoMap(prev => {
      const m = { ...prev };
      counts.forEach(c => { if (c.photo_filename) m[c.item_id] = c.photo_filename; });
      return m;
    });
  }, [counts]);
  const [photoBusyId, setPhotoBusyId] = useState(null);
  const [photoBust, setPhotoBust] = useState(0);

  const uploadPhoto = async (itemId, file) => {
    setPhotoBusyId(itemId);
    try {
      const compressed = await compressImage(file).catch(() => file);
      const fd = new FormData();
      fd.append('item_id', itemId);
      fd.append('location', location);
      fd.append('week_start', weekStart);
      fd.append('photo', compressed, compressed.name || 'photo.jpg');
      const r = await fetch(`${API}/api/spirits/counts/photo`, { method: 'POST', credentials: 'include', body: fd });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).message || 'Photo upload failed.');
      const row = await r.json();
      setPhotoMap(prev => ({ ...prev, [itemId]: row.photo_filename }));
      setPhotoBust(b => b + 1);
    } catch (e) { toast.error(e.message); }
    finally { setPhotoBusyId(null); }
  };

  // null | { state, diff } where diff = counted − ceiling.
  const discFor = (item, bottles, hasCount) => {
    const d = disc[item.id];
    if (!hasCount || !d || !d.has_prev) return null;
    const ceiling = parseFloat(d.ceiling_bottles) || 0;
    const floor = parseFloat(d.floor_bottles) || 0;
    if (ceiling === 0) return null;
    if (bottles > ceiling) return { state: 'red', diff: bottles - ceiling };
    if (ceiling - bottles > floor) return { state: 'orange', diff: bottles - ceiling };
    return null;
  };

  // Flag badge + optional recount photo control, shown only when a count is flagged
  // (or already has a photo). Visible to counters too — it's un-gated, like the count.
  const renderDiscFlag = (item, bottles, hasCount) => {
    if (!showDiscrepancy) return null;
    const d = discFor(item, bottles, hasCount);
    const hasP = !!photoMap[item.id];
    if (!d && !hasP) return null;
    return (
      <div className="mt-1 flex flex-wrap items-center gap-2">
        {d && (
          <span className={`text-[10px] px-1.5 py-0.5 rounded ${d.state === 'red' ? 'bg-red-500/15 text-red-400' : 'bg-orange-500/15 text-orange-400'}`}>
            {d.state === 'red' ? `↑ ${d.diff} over expected — verify` : `↓ ${Math.abs(d.diff)} under expected — recount`}
          </span>
        )}
        <label className="text-[10px] text-orange-400 hover:text-orange-300 cursor-pointer whitespace-nowrap">
          {photoBusyId === item.id ? '📷 …' : (hasP ? '📷 Replace' : '📷 Add photo')}
          <input type="file" accept="image/*" capture="environment" className="hidden"
            onChange={e => { const f = e.target.files?.[0]; if (f) uploadPhoto(item.id, f); e.target.value = ''; }} />
        </label>
        {hasP && <SpiritsPhotoThumb location={location} weekStart={weekStart} itemId={item.id} bust={photoBust} />}
      </div>
    );
  };

  const rowFor = (item) => {
    const bpc = Math.max(1, parseInt(item.bottles_per_case, 10) || 12);
    const c = countById.get(item.id);
    const display = c ? parseInt(c.display_qty, 10) || 0 : 0;
    const storage = c ? parseInt(c.storage_qty, 10) || 0 : 0;
    const hasCount = !!c;
    const bottles = display + storage;
    const casesOnHand = bottles / bpc;
    const par = parById.get(item.id); // in cases
    const extra = extraById.get(item.id) || 0; // in cases
    const auto = par != null ? Math.max(0, Math.ceil(par - casesOnHand)) : 0;
    const target = auto + extra;
    const production = item.production_quantity != null ? parseFloat(item.production_quantity) : null; // in cases
    const order = production != null ? Math.min(target, production) : target;
    const capped = production != null && target > production;
    return { c, bpc, display, storage, hasCount, bottles, casesOnHand, par, extra, auto, order, capped, production };
  };

  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <input type="text" value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Search spirits…"
          className="flex-1 max-w-xs bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm" />
        <div className="flex items-center gap-3">
          {total > 0 && (
            <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${
              isComplete ? 'bg-green-500/15 text-green-400 border border-green-500/30'
                         : 'bg-amber-500/10 text-amber-400 border border-amber-500/30'
            }`}>
              {isComplete ? '✓ Complete' : `${counted} / ${total} counted`}
            </span>
          )}
          <span className="text-gray-500 text-xs">Week of {fmtWeekRange(weekStart)}</span>
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="bg-gray-800 border border-gray-700 rounded-xl py-12 text-center text-gray-500 text-sm">
          {items.length === 0
            ? 'No spirits yet. A manager needs to sync the distillery inventory in Manage → Settings.'
            : 'No spirits match that search.'}
        </div>
      ) : (
        <>
          {/* Mobile cards */}
          <div className="sm:hidden space-y-2">
            {filtered.map(item => {
              const { c, bpc, display, storage, hasCount, casesOnHand, par, extra, order, capped } = rowFor(item);
              return (
                <div key={item.id} className="bg-gray-800 border border-gray-700 rounded-xl p-3">
                  <div className="mb-2">
                    <p className="text-white text-sm font-medium truncate">{item.name}</p>
                    <p className="text-gray-500 text-[10px]">{bpc}/case</p>
                    {renderDiscFlag(item, display + storage, hasCount)}
                  </div>
                  <div className="grid grid-cols-2 gap-3 mb-1">
                    <div>
                      <label className="text-gray-500 text-[10px] uppercase tracking-wider block mb-1">Display Shelves</label>
                      {countsEditable
                        ? <WheelCountCell value={c ? display : null} onSave={q => handleSaveArea(item.id, 'display', q)} label="Display Shelves" itemName={item.name} />
                        : <div className="w-full rounded-lg border border-gray-700 bg-gray-800 px-3 py-2 text-right text-lg font-semibold tabular-nums text-white">{c ? display : '–'}</div>}
                    </div>
                    <div>
                      <label className="text-gray-500 text-[10px] uppercase tracking-wider block mb-1">Storage</label>
                      {countsEditable
                        ? <WheelCountCell value={c ? storage : null} onSave={q => handleSaveArea(item.id, 'storage', q)} label="Storage" itemName={item.name} />
                        : <div className="w-full rounded-lg border border-gray-700 bg-gray-800 px-3 py-2 text-right text-lg font-semibold tabular-nums text-white">{c ? storage : '–'}</div>}
                    </div>
                  </div>
                  {canUpload && (
                    <>
                      <div className="flex items-center justify-between text-xs mt-3 pt-2 border-t border-gray-700">
                        <span className="text-gray-500">Cases: <span className="text-gray-300">{hasCount ? fmtQty(Math.round(casesOnHand * 100) / 100) : '—'}</span></span>
                        <span className="text-gray-500">Par: <span className="text-gray-300">{par != null ? fmtQty(par) : '—'}</span></span>
                        {item.production_quantity != null && (
                          <span className="text-gray-500">Avail: <span className="text-gray-300">{fmtQty(item.production_quantity)}</span></span>
                        )}
                      </div>
                      <div className="flex items-center justify-between gap-3 mt-2">
                        <div>
                          <p className="text-gray-500 text-[10px] uppercase tracking-wider">Order (cases)</p>
                          <p className={`text-base font-bold ${order > 0 ? 'text-orange-400' : 'text-gray-500'}`}>
                            {fmtQty(order)}
                            {capped && <span className="ml-1 text-[10px] font-normal text-amber-400" title="Capped at production availability">capped</span>}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-gray-500 text-[10px] uppercase tracking-wider">+ Extra (cases)</p>
                          {extrasEditable
                            ? <ExtraQtyCell value={extra} onSave={v => handleSaveExtra(item.id, v)} />
                            : <p className={`text-base font-semibold ${extra > 0 ? 'text-orange-400' : 'text-gray-500'}`}>{extra > 0 ? fmtQty(extra) : '—'}</p>}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>

          {/* Desktop table */}
          <div className="hidden sm:block bg-gray-800 rounded-xl border border-gray-700 overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-700">
                  <th className="text-left text-gray-400 text-xs font-medium uppercase tracking-wider px-5 py-3">Spirit</th>
                  <th className="text-right text-gray-400 text-xs font-medium uppercase tracking-wider px-5 py-3">Display Shelves <span className="normal-case text-[10px] text-gray-500">(bottles)</span></th>
                  <th className="text-right text-gray-400 text-xs font-medium uppercase tracking-wider px-5 py-3">Storage <span className="normal-case text-[10px] text-gray-500">(bottles)</span></th>
                  {canUpload && (
                    <>
                      <th className="text-right text-gray-400 text-xs font-medium uppercase tracking-wider px-5 py-3">Cases</th>
                      <th className="text-right text-gray-400 text-xs font-medium uppercase tracking-wider px-5 py-3">Par <span className="normal-case text-[10px] text-gray-500">(cases)</span></th>
                      <th className="text-right text-gray-400 text-xs font-medium uppercase tracking-wider px-5 py-3">Order <span className="normal-case text-[10px] text-gray-500">(cases)</span></th>
                      <th className="text-right text-gray-400 text-xs font-medium uppercase tracking-wider px-5 py-3">+ Extra <span className="normal-case text-[10px] text-gray-500">(cases)</span></th>
                      <th className="text-right text-gray-400 text-xs font-medium uppercase tracking-wider px-5 py-3 hidden lg:table-cell">In Production <span className="normal-case text-[10px] text-gray-500">(cases)</span></th>
                    </>
                  )}
                </tr>
              </thead>
              <tbody>
                {filtered.map(item => {
                  const { c, bpc, display, storage, hasCount, bottles, casesOnHand, par, extra, order, capped } = rowFor(item);
                  return (
                    <tr key={item.id} className="border-b border-gray-700/50 last:border-0 hover:bg-gray-700/20 transition">
                      <td className="px-5 py-2.5 text-white font-medium">
                        {item.name}
                        <span className="ml-2 text-[10px] text-gray-500">{bpc}/case</span>
                        {renderDiscFlag(item, bottles, hasCount)}
                      </td>
                      <td className="px-5 py-2.5 text-right">
                        {countsEditable
                          ? <CountCell value={c ? display : null} onSave={q => handleSaveArea(item.id, 'display', q)} />
                          : <span className="text-white text-sm tabular-nums">{c ? display : '—'}</span>}
                      </td>
                      <td className="px-5 py-2.5 text-right">
                        {countsEditable
                          ? <CountCell value={c ? storage : null} onSave={q => handleSaveArea(item.id, 'storage', q)} />
                          : <span className="text-white text-sm tabular-nums">{c ? storage : '—'}</span>}
                      </td>
                      {canUpload && (
                        <>
                          <td className="px-5 py-2.5 text-right text-white text-sm font-semibold" title={hasCount ? `${bottles} bottles` : ''}>
                            {hasCount ? fmtQty(Math.round(casesOnHand * 100) / 100) : '—'}
                          </td>
                          <td className="px-5 py-2.5 text-right text-gray-300 text-sm">{par != null ? fmtQty(par) : '—'}</td>
                          <td className={`px-5 py-2.5 text-right text-sm font-bold ${order > 0 ? 'text-orange-400' : 'text-gray-500'}`}>
                            {fmtQty(order)}
                            {capped && (
                              <span className="ml-1.5 text-[10px] font-normal text-amber-400 uppercase tracking-wider" title="Capped at production availability">capped</span>
                            )}
                          </td>
                          <td className="px-5 py-2.5 text-right">
                            {extrasEditable
                              ? <ExtraQtyCell value={extra} onSave={v => handleSaveExtra(item.id, v)} />
                              : <span className={`text-sm font-semibold ${extra > 0 ? 'text-orange-400' : 'text-gray-500'}`}>{extra > 0 ? fmtQty(extra) : '—'}</span>}
                          </td>
                          <td className="px-5 py-2.5 text-right text-gray-400 text-sm hidden lg:table-cell">
                            {item.production_quantity != null ? fmtQty(item.production_quantity) : '—'}
                          </td>
                        </>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </>
  );
}

// ── Order email modal ────────────────────────────────────────────────────────

function SendOrderModal({ location, locationLabel, weekStart, onClose, onSent }) {
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [overrides, setOverrides] = useState({});
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    Promise.all([
      fetch(`${API}/api/spirits/order-preview?location=${location}&week_start=${weekStart}`, { credentials: 'include' }).then(r => r.ok ? r.json() : Promise.reject()),
      fetch(`${API}/api/spirits/inventory-status?location=${location}&week_start=${weekStart}`, { credentials: 'include' }).then(r => r.ok ? r.json() : Promise.reject()),
    ])
      .then(([preview, st]) => {
        setRows(preview.rows || []);
        const init = {};
        (preview.rows || []).forEach(r => { if (r.needed > 0) init[r.id] = r.needed; });
        setOverrides(init);
        setStatus(st);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [location, weekStart]);

  const handleSend = async () => {
    setSending(true); setError('');
    const r = await fetch(`${API}/api/spirits/send-order`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ location, week_start: weekStart, overrides, force: status && !status.is_complete }),
    });
    const data = await r.json();
    setSending(false);
    if (!r.ok) { setError(data.message || 'Failed to send'); return; }
    onSent(data.message || 'Order sent.');
    onClose();
  };

  const toOrder = rows.filter(r => (overrides[r.id] ?? r.needed) > 0);
  const incomplete = status && !status.is_complete;

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-end sm:items-center justify-center sm:p-4" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="bg-gray-800 rounded-t-2xl sm:rounded-xl shadow-xl w-full sm:max-w-lg max-h-[92vh] flex flex-col">
        <div className="flex items-center justify-between p-5 border-b border-gray-700">
          <div>
            <h2 className="text-white font-semibold">Send Spirits Order</h2>
            <p className="text-gray-400 text-xs mt-0.5">{locationLabel} · Week of {fmtWeekRange(weekStart)}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white text-lg">✕</button>
        </div>
        <div className="overflow-y-auto flex-1 p-5">
          {incomplete && (
            <div className="mb-4 px-3 py-2.5 rounded-lg bg-amber-500/10 border border-amber-500/40 text-amber-300 text-xs">
              <strong>Inventory incomplete:</strong> {status.counted} of {status.total} spirits counted. Sending now will still go out — the auto-order won't fire on Tuesday until the count is done.
            </div>
          )}
          {loading ? (
            <p className="text-gray-500 text-sm text-center py-8">Loading…</p>
          ) : toOrder.length === 0 ? (
            <p className="text-gray-500 text-sm text-center py-8">Nothing needs to be ordered this week.</p>
          ) : (
            <div className="space-y-2">
              {toOrder.map(r => (
                <div key={r.id} className="flex items-center gap-3 bg-gray-700/40 rounded-lg px-3 py-2">
                  <div className="flex-1 min-w-0">
                    <p className="text-white text-sm font-medium truncate">{r.name}</p>
                    <p className="text-gray-500 text-xs">
                      Par {fmtQty(r.par)} · Count {fmtQty(r.count)}
                      {r.production_quantity != null && ` · Avail ${fmtQty(r.production_quantity)}`}
                      {r.capped && <span className="text-amber-400"> · capped at production</span>}
                    </p>
                  </div>
                  <input type="number" min="0" step="0.5"
                    value={overrides[r.id] ?? 0}
                    onChange={e => setOverrides(p => ({ ...p, [r.id]: e.target.value }))}
                    className="w-20 bg-gray-700 border border-gray-600 rounded-lg px-2 py-1.5 text-white text-sm text-right" />
                </div>
              ))}
            </div>
          )}
          {error && <p className="text-red-400 text-sm mt-3">{error}</p>}
        </div>
        <div className="flex gap-2 justify-end p-5 border-t border-gray-700">
          <button onClick={onClose} className="text-sm text-gray-400 hover:text-white px-4 py-2">Cancel</button>
          <button onClick={handleSend} disabled={sending || loading}
            className="text-sm font-medium px-5 py-2 rounded-lg text-white disabled:opacity-50"
            style={{ backgroundColor: '#F05A28' }}>
            {sending ? 'Sending…' : incomplete ? 'Send Anyway' : 'Send Email'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Manage tab ───────────────────────────────────────────────────────────────

function ManageTab({ location, locationLabel, items, pars, settings, isAdmin, onChange, onItemsChanged }) {
  const [section, setSection] = useState('items');
  const [showHidden, setShowHidden] = useState(false);

  const sBtn = (s, label) => (
    <button onClick={() => setSection(s)}
      className={`px-4 py-2 text-sm font-medium rounded-lg transition ${section === s ? 'bg-gray-600 text-white' : 'text-gray-400 hover:text-white'}`}>
      {label}
    </button>
  );

  return (
    <div>
      <p className="text-gray-500 text-xs mb-4">Pars are now set right on the Order screen (tap a par to edit). Manage the item list and settings here.</p>
      <div className="flex flex-wrap gap-2 mb-5">
        {sBtn('items', 'Items')}
        {isAdmin && sBtn('settings', 'Settings')}
      </div>

      {section === 'items' && (
        <ItemsManager
          items={items}
          showHidden={showHidden}
          onShowHiddenToggle={() => setShowHidden(s => !s)}
          onChanged={onItemsChanged}
        />
      )}

      {section === 'settings' && isAdmin && (
        <SettingsSection settings={settings} onChanged={() => { onChange(); onItemsChanged(); }} />
      )}
    </div>
  );
}

// ── Items manager ────────────────────────────────────────────────────────────

function ItemsManager({ items, showHidden, onShowHiddenToggle, onChanged }) {
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState(null); // item or 'new'
  const filtered = items.filter(i =>
    (showHidden || !i.hidden) &&
    (!search || i.name.toLowerCase().includes(search.toLowerCase()))
  );
  const hiddenCount = items.filter(i => i.hidden).length;

  const handleDelete = async (item) => {
    if (!window.confirm(`Permanently delete "${item.name}"?`)) return;
    await fetch(`${API}/api/spirits/items/${item.id}`, { method: 'DELETE', credentials: 'include' });
    onChanged();
  };

  return (
    <>
      <div className="flex items-center justify-between gap-3 mb-3">
        <input type="text" value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Search…"
          className="flex-1 max-w-xs bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm" />
        <div className="flex items-center gap-3">
          <label className="text-xs text-gray-400 flex items-center gap-2 cursor-pointer"
            title="Items with 0 on hand at every taproom and none in production are hidden automatically. They reappear on their own when a taproom counts some or production returns.">
            <input type="checkbox" checked={showHidden} onChange={onShowHiddenToggle} className="accent-orange-500" />
            Show depleted ({hiddenCount})
          </label>
          <button onClick={() => setEditing('new')}
            className="text-sm font-medium px-3 py-2 rounded-lg text-white"
            style={{ backgroundColor: '#F05A28' }}>+ Add Item</button>
        </div>
      </div>
      <div className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden">
        {filtered.length === 0 ? (
          <p className="text-gray-500 text-sm px-5 py-6">No items.</p>
        ) : (
          <ul>
            {filtered.map(item => (
              <li key={item.id} className={`flex items-center gap-3 px-4 sm:px-5 py-3 border-b border-gray-700 last:border-0 ${item.hidden ? 'opacity-60' : ''}`}>
                <div className="flex-1 min-w-0">
                  <p className="text-white text-sm font-medium truncate">
                    {item.name}
                    {item.hidden && <span className="ml-2 text-xs uppercase tracking-wider text-gray-500">depleted</span>}
                  </p>
                  <p className="text-gray-500 text-xs">
                    {[item.category, item.unit_size].filter(Boolean).join(' · ') || '—'}
                    {item.production_quantity != null && ` · ${fmtQty(item.production_quantity)} in production`}
                  </p>
                </div>
                <div className="flex gap-3 flex-shrink-0">
                  <button onClick={() => setEditing(item)} className="text-xs text-gray-400 hover:text-white">Edit</button>
                  <button onClick={() => handleDelete(item)} className="text-xs text-red-500 hover:text-red-400">Delete</button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
      {editing && (
        <ItemModal
          item={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); onChanged(); }}
        />
      )}
    </>
  );
}

function ItemModal({ item, onClose, onSaved }) {
  const isEdit = !!item;
  const [form, setForm] = useState({
    name: item?.name || '',
    category: item?.category || '',
    unit_size: item?.unit_size || '',
    bottles_per_case: item?.bottles_per_case ?? 12,
    disc_floor_bottles: item?.disc_floor_bottles ?? '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const set = f => e => setForm(p => ({ ...p, [f]: e.target.value }));

  const handleSave = async () => {
    if (!form.name.trim()) return setError('Name is required');
    const bpc = parseInt(form.bottles_per_case, 10);
    if (isNaN(bpc) || bpc < 1) return setError('Bottles per case must be at least 1');
    setSaving(true); setError('');
    const url = isEdit ? `${API}/api/spirits/items/${item.id}` : `${API}/api/spirits/items`;
    const r = await fetch(url, {
      method: isEdit ? 'PATCH' : 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        name: form.name.trim(),
        category: form.category.trim(),
        unit_size: form.unit_size.trim(),
        bottles_per_case: bpc,
        disc_floor_bottles: form.disc_floor_bottles === '' ? '' : parseFloat(form.disc_floor_bottles),
      }),
    });
    setSaving(false);
    if (r.ok) onSaved();
    else { const d = await r.json(); setError(d.message || 'Error'); }
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-end sm:items-center justify-center sm:p-4" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="bg-gray-800 rounded-t-2xl sm:rounded-xl shadow-xl w-full sm:max-w-md">
        <div className="flex items-center justify-between p-5 border-b border-gray-700">
          <h2 className="text-white font-semibold">{isEdit ? 'Edit Item' : 'Add Item'}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white text-lg">✕</button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="text-xs text-gray-400 block mb-1">Name *</label>
            <input autoFocus type="text" value={form.name} onChange={set('name')}
              className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-gray-400 block mb-1">Category</label>
              <input type="text" value={form.category} onChange={set('category')}
                placeholder="e.g. Whiskey"
                className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm" />
            </div>
            <div>
              <label className="text-xs text-gray-400 block mb-1">Unit Size</label>
              <input type="text" value={form.unit_size} onChange={set('unit_size')}
                placeholder="e.g. 750ml"
                className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm" />
            </div>
          </div>
          <div>
            <label className="text-xs text-gray-400 block mb-1">Bottles per Case *</label>
            <input type="number" min="1" step="1" value={form.bottles_per_case} onChange={set('bottles_per_case')}
              className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm" />
            <p className="text-gray-500 text-[10px] mt-1">Used to convert weekly bottle counts into cases. For kegs, use 1.</p>
          </div>
          <div>
            <label className="text-xs text-gray-400 block mb-1">Discrepancy floor (bottles)</label>
            <input type="number" min="0" step="1" value={form.disc_floor_bottles} onChange={set('disc_floor_bottles')}
              placeholder="use global default"
              className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm placeholder-gray-500" />
            <p className="text-gray-500 text-[10px] mt-1">Flag a recount if this item drops more than this many bottles below last week + what was ordered. Blank = use the global default.</p>
          </div>
          {error && <p className="text-red-400 text-sm">{error}</p>}
          <div className="flex gap-2 justify-end pt-1">
            <button onClick={onClose} className="text-sm text-gray-400 hover:text-white px-4 py-2">Cancel</button>
            <button onClick={handleSave} disabled={saving}
              className="text-sm font-medium px-5 py-2 rounded-lg text-white disabled:opacity-50"
              style={{ backgroundColor: '#F05A28' }}>
              {saving ? 'Saving…' : isEdit ? 'Save Changes' : 'Add Item'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Settings section: API URL + sync + email recipients ──────────────────────

function RecipientBox({ title, description, endpointPath, placeholder }) {
  const [recipients, setRecipients] = useState([]);
  const [newEmail, setNewEmail] = useState('');
  const [adding, setAdding] = useState(false);
  const [err, setErr] = useState('');

  const load = useCallback(async () => {
    const r = await fetch(`${API}${endpointPath}`, { credentials: 'include' });
    if (r.ok) setRecipients(await r.json());
  }, [endpointPath]);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!newEmail.trim()) return;
    setAdding(true); setErr('');
    const r = await fetch(`${API}${endpointPath}`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: newEmail.trim() }),
    });
    setAdding(false);
    if (r.ok) { setNewEmail(''); load(); }
    else { const d = await r.json(); setErr(d.message || 'Error'); }
  };

  const remove = async (id) => {
    await fetch(`${API}${endpointPath}/${id}`, { method: 'DELETE', credentials: 'include' });
    load();
  };

  return (
    <div className="bg-gray-800 border border-gray-700 rounded-xl p-5 space-y-4">
      <h3 className="text-white font-semibold">{title}</h3>
      <p className="text-gray-500 text-xs">{description}</p>
      <div className="space-y-2">
        {recipients.length === 0 ? (
          <p className="text-gray-500 text-sm">No recipients yet.</p>
        ) : recipients.map(r => (
          <div key={r.id} className="flex items-center justify-between bg-gray-700/40 rounded-lg px-3 py-2">
            <span className="text-gray-300 text-sm">{r.email}</span>
            <button onClick={() => remove(r.id)} className="text-xs text-red-400 hover:text-red-300">Remove</button>
          </div>
        ))}
      </div>
      <div className="flex gap-2">
        <input type="email" value={newEmail} onChange={e => setNewEmail(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && add()}
          placeholder={placeholder}
          className="flex-1 bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm" />
        <button onClick={add} disabled={adding || !newEmail.trim()}
          className="text-sm font-medium px-4 py-2 rounded-lg text-white disabled:opacity-50"
          style={{ backgroundColor: '#F05A28' }}>
          Add
        </button>
      </div>
      {err && <p className="text-xs text-red-400">{err}</p>}
    </div>
  );
}

function SettingsSection({ settings, onChanged }) {
  const [apiUrl, setApiUrl] = useState(settings?.distillery_api_url || '');
  const [apiToken, setApiToken] = useState('');
  const [savingUrl, setSavingUrl] = useState(false);
  const [savingToken, setSavingToken] = useState(false);
  const [tokenMsg, setTokenMsg] = useState('');
  const [syncing, setSyncing] = useState(false);
  const [syncMsg, setSyncMsg] = useState('');
  const [floor, setFloor] = useState(settings?.discrepancy_floor_bottles ?? 6);
  const [savingFloor, setSavingFloor] = useState(false);
  const [floorMsg, setFloorMsg] = useState('');

  useEffect(() => { setApiUrl(settings?.distillery_api_url || ''); }, [settings]);
  useEffect(() => { if (settings?.discrepancy_floor_bottles != null) setFloor(settings.discrepancy_floor_bottles); }, [settings]);

  const saveFloor = async () => {
    setSavingFloor(true); setFloorMsg('');
    const r = await fetch(`${API}/api/spirits/settings`, {
      method: 'PATCH', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ discrepancy_floor_bottles: parseFloat(floor) || 0 }),
    });
    setSavingFloor(false);
    if (r.ok) { setFloorMsg('Saved.'); onChanged(); } else { setFloorMsg('Could not save.'); }
  };

  const saveUrl = async () => {
    setSavingUrl(true);
    await fetch(`${API}/api/spirits/settings`, {
      method: 'PATCH', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ distillery_api_url: apiUrl }),
    });
    setSavingUrl(false);
    onChanged();
  };

  const saveToken = async () => {
    setSavingToken(true); setTokenMsg('');
    const r = await fetch(`${API}/api/spirits/settings`, {
      method: 'PATCH', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ distillery_api_token: apiToken }),
    });
    setSavingToken(false);
    if (r.ok) {
      setApiToken('');
      setTokenMsg(apiToken.trim() ? 'Token saved.' : 'Token cleared.');
      onChanged();
    } else {
      setTokenMsg('Could not save token.');
    }
  };

  const sync = async () => {
    setSyncing(true); setSyncMsg('');
    const r = await fetch(`${API}/api/spirits/items/sync`, {
      method: 'POST', credentials: 'include',
    });
    const d = await r.json();
    setSyncing(false);
    if (r.ok) {
      setSyncMsg(`Synced — added ${d.added}, updated ${d.updated}${d.depleted ? `, zeroed ${d.depleted} depleted` : ''}.`);
      onChanged();
    } else {
      setSyncMsg(d.message || 'Sync failed');
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 max-w-4xl">
      <div className="bg-gray-800 border border-gray-700 rounded-xl p-5 space-y-4">
        <h3 className="text-white font-semibold">Distillery Inventory Feed</h3>
        <p className="text-gray-500 text-xs">The Distillogy finished-goods endpoint. Sync pages through it and sums each product's shippable (released, not on-hold) cases into its available quantity.</p>
        <div>
          <label className="text-xs text-gray-400 block mb-1">API URL</label>
          <input type="url" value={apiUrl} onChange={e => setApiUrl(e.target.value)}
            placeholder="https://api.distillogy.com/api/v1/finished-goods"
            className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm" />
        </div>
        <button onClick={saveUrl} disabled={savingUrl}
          className="text-sm px-4 py-2 rounded-lg text-gray-300 bg-gray-700 hover:bg-gray-600 disabled:opacity-50">
          {savingUrl ? 'Saving…' : 'Save URL'}
        </button>
        <div>
          <label className="text-xs text-gray-400 block mb-1">
            API Token {settings?.has_token
              ? <span className="text-green-400">— a token is set</span>
              : <span className="text-red-400">— no token set</span>}
          </label>
          <input type="password" value={apiToken} onChange={e => setApiToken(e.target.value)}
            autoComplete="off"
            placeholder={settings?.has_token ? 'Enter a new token to replace it' : 'Bearer token'}
            className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm" />
          <p className="text-[11px] text-gray-600 mt-1">Sent as <code className="text-gray-500">Authorization: Bearer …</code>. Stored server-side; never shown again. Save an empty field to clear.</p>
        </div>
        <div className="flex gap-2 items-center">
          <button onClick={saveToken} disabled={savingToken}
            className="text-sm px-4 py-2 rounded-lg text-gray-300 bg-gray-700 hover:bg-gray-600 disabled:opacity-50">
            {savingToken ? 'Saving…' : 'Save Token'}
          </button>
          {tokenMsg && <span className="text-xs text-gray-400">{tokenMsg}</span>}
        </div>
        <div className="pt-1">
          <button onClick={sync} disabled={syncing || !settings?.distillery_api_url || !settings?.has_token}
            className="text-sm font-medium px-4 py-2 rounded-lg text-white disabled:opacity-50"
            style={{ backgroundColor: '#F05A28' }}>
            {syncing ? 'Syncing…' : 'Sync Now'}
          </button>
        </div>
        {syncMsg && <p className="text-sm text-gray-300">{syncMsg}</p>}
        {settings?.last_sync_at && <p className="text-xs text-gray-500">Last synced: {fmtDateTime(settings.last_sync_at)}</p>}
        {settings?.last_sync_error && <p className="text-xs text-red-400">Last error: {settings.last_sync_error}</p>}
      </div>

      <div className="bg-gray-800 border border-gray-700 rounded-xl p-5 space-y-4">
        <h3 className="text-white font-semibold">Discrepancy Detection</h3>
        <p className="text-gray-500 text-xs">
          When a weekly count is above what's possible (last week + what was ordered) it's always flagged. When it drops more
          than this floor below that ceiling, it's flagged for a recount + optional photo. Set a per-item override in Manage → Items.
        </p>
        <div>
          <label className="text-xs text-gray-400 block mb-1">Global floor (bottles)</label>
          <input type="number" min="0" step="1" value={floor} onChange={e => setFloor(e.target.value)}
            className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm" />
        </div>
        <div className="flex gap-2 items-center">
          <button onClick={saveFloor} disabled={savingFloor}
            className="text-sm font-medium px-4 py-2 rounded-lg text-white disabled:opacity-50"
            style={{ backgroundColor: '#F05A28' }}>
            {savingFloor ? 'Saving…' : 'Save Floor'}
          </button>
          {floorMsg && <span className="text-xs text-gray-400">{floorMsg}</span>}
        </div>
      </div>

      <RecipientBox
        title="Order Email Recipients"
        description="Everyone here gets the weekly spirits order email — sent automatically Tuesdays 2 PM ET, or manually when a manager hits Send."
        endpointPath="/api/spirits/email-recipients"
        placeholder="distilling@ologybrewing.com"
      />

      <RecipientBox
        title="Inventory Reminder Recipients"
        description="Emailed Tuesdays around 1 AM ET if a location hasn't finished its weekly count. Auto-orders will not go out for locations that aren't complete."
        endpointPath="/api/spirits/notification-recipients"
        placeholder="manager@ologybrewing.com"
      />

      {settings?.last_sent_at && (
        <div className="lg:col-span-2 text-xs text-gray-500">Last order email sent: {fmtDateTime(settings.last_sent_at)}</div>
      )}
    </div>
  );
}

// ── Redesigned manager Order screen ─────────────────────────────────────────

// Whole-case +/- stepper for the per-item Extra.
function OrderStepper({ value, onChange, disabled }) {
  const v = value || 0;
  return (
    <span className="inline-flex items-center border border-gray-600 rounded-lg overflow-hidden">
      <button type="button" disabled={disabled || v <= 0} onClick={() => onChange(v - 1)}
        className="w-8 h-8 bg-gray-700 text-white text-lg leading-none disabled:opacity-40 hover:bg-orange-500 transition">−</button>
      <span className="w-9 text-center text-sm font-semibold tabular-nums text-white">{v}</span>
      <button type="button" disabled={disabled} onClick={() => onChange(v + 1)}
        className="w-8 h-8 bg-gray-700 text-white text-lg leading-none disabled:opacity-40 hover:bg-orange-500 transition">+</button>
    </span>
  );
}

// Whole-bottle par input (blurs/enters to commit).
function ParInput({ value, onSave }) {
  const [v, setV] = useState(value ?? '');
  useEffect(() => { setV(value ?? ''); }, [value]);
  const commit = () => { const n = Math.max(0, Math.round(parseFloat(v) || 0)); setV(n); if (n !== (value ?? 0)) onSave(n); };
  return (
    <input type="number" min="0" step="1" inputMode="numeric" value={v}
      onChange={e => setV(e.target.value)} onBlur={commit}
      onKeyDown={e => { if (e.key === 'Enter') e.currentTarget.blur(); }}
      className="w-16 bg-gray-700 border border-gray-600 rounded-lg px-2 py-1.5 text-white text-sm text-right tabular-nums focus:outline-none focus:ring-2 focus:ring-orange-500" />
  );
}

// The single screen a manager lands on: review this week's order and send it.
function OrderScreen({ location, locationLabel, items, counts, extras, pars, weekStart,
    currentWeek, isCurrentWeek, weekOptions, onWeekChange, onExtraSaved, onParSaved, onShowSend }) {
  const toast = useToast();
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('toorder'); // 'toorder' | 'all'
  const [expanded, setExpanded] = useState({});
  const [restOpen, setRestOpen] = useState(false);

  const countById = new Map(counts.map(c => [c.item_id, c]));
  const extraById = new Map(extras.map(e => [e.item_id, parseFloat(e.extra_qty)]));
  const parById = new Map(pars.map(p => [p.item_id, p.par_bottles != null ? parseFloat(p.par_bottles) : null]));

  const compute = (item) => {
    const bpc = Math.max(1, parseInt(item.bottles_per_case, 10) || 12);
    const c = countById.get(item.id);
    const display = c ? parseInt(c.display_qty, 10) || 0 : 0;
    const storage = c ? parseInt(c.storage_qty, 10) || 0 : 0;
    const hasCount = !!c;
    const bottles = display + storage;
    const parB = parById.get(item.id) ?? 0;            // par in bottles
    const extra = extraById.get(item.id) || 0;         // cases
    const shortBottles = Math.max(0, parB - bottles);
    const auto = Math.ceil(shortBottles / bpc);        // whole cases
    const target = auto + extra;
    const production = item.production_quantity != null ? parseFloat(item.production_quantity) : null; // cases
    const order = production != null ? Math.min(target, production) : target;
    const capped = production != null && target > production;
    return { bpc, display, storage, hasCount, bottles, parB, extra, auto, order, capped, production, shortBottles, casesOnHand: bottles / bpc };
  };

  const rows = items.map(it => ({ it, m: compute(it) }));
  const match = ({ it }) => !search || it.name.toLowerCase().includes(search.toLowerCase());
  const toOrder = rows.filter(r => r.m.order > 0 && match(r));
  const rest = rows.filter(r => r.m.order === 0 && match(r));
  const shown = filter === 'all' ? rows.filter(match) : toOrder;

  const totalCases = toOrder.reduce((s, r) => s + r.m.order, 0);
  const total = items.length;
  const counted = counts.length;
  const isComplete = total > 0 && counted >= total;

  // PATCH an extra (null clears the override), then update parent state.
  const saveExtra = async (itemId, cases) => {
    const clearing = !cases || cases <= 0;
    const r = await fetch(`${API}/api/spirits/order-extra`, {
      method: 'PATCH', credentials: 'include', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ item_id: itemId, location, week_start: weekStart, extra_qty: clearing ? null : cases }),
    });
    if (r.ok) onExtraSaved(itemId, clearing ? null : cases);
    else toast.error('Could not save extra.');
  };

  const savePar = async (itemId, bottles) => {
    const r = await fetch(`${API}/api/spirits/pars`, {
      method: 'PATCH', credentials: 'include', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ item_id: itemId, location, par_bottles: bottles }),
    });
    if (r.ok) onParSaved(itemId, bottles);
    else toast.error('Could not save par.');
  };

  const toggle = (id) => setExpanded(p => ({ ...p, [id]: !p[id] }));

  const renderRow = ({ it, m }) => {
    const isOpen = !!expanded[it.id];
    return (
      <div key={it.id} className={`bg-gray-800 border rounded-xl px-3 py-3 sm:px-4 ${m.order > 0 ? 'border-gray-700' : 'border-gray-700/60 opacity-70'}`}>
        <div className="flex items-start gap-3 sm:gap-5 flex-wrap sm:flex-nowrap">
          <div className="flex-1 min-w-0 basis-full sm:basis-auto">
            <p className="text-white font-medium truncate">{it.name}</p>
            <p className="text-gray-500 text-[11px]">{it.category || '—'} · {m.bpc}/case</p>
          </div>

          <button type="button" onClick={() => toggle(it.id)} className="text-left sm:text-right shrink-0">
            <span className="block text-gray-500 text-[10px] uppercase tracking-wider">On hand</span>
            <span className="text-white text-sm">
              <span className="font-semibold tabular-nums">{m.hasCount ? m.bottles : '–'}</span>
              <span className="text-gray-400"> bottles</span>
              <span className="text-gray-500 ml-1">{isOpen ? '▾' : '▸'}</span>
            </span>
          </button>

          <div className="text-left sm:text-right shrink-0">
            <span className="block text-gray-500 text-[10px] uppercase tracking-wider">Par (bottles)</span>
            {isCurrentWeek
              ? <ParInput value={m.parB} onSave={(n) => savePar(it.id, n)} />
              : <span className="text-gray-300 text-sm tabular-nums">{m.parB}</span>}
          </div>

          <div className="text-left sm:text-right shrink-0 min-w-[150px]">
            <span className="block text-gray-500 text-[10px] uppercase tracking-wider">Order</span>
            <span className={`text-lg font-bold tabular-nums ${m.order > 0 ? 'text-orange-400' : 'text-gray-500'}`}>
              {m.order > 0 ? `${m.order} ${m.order === 1 ? 'case' : 'cases'}` : '—'}
            </span>
            <span className="block text-gray-500 text-[11px]">
              {m.order > 0 ? `short ${m.shortBottles} btl · rec ${fmtQty(m.auto)}` : 'at par'}
              {m.extra ? ` + ${fmtQty(m.extra)} extra` : ''}
              {m.capped ? <span className="text-amber-400"> · only {fmtQty(m.production)} available</span> : ''}
            </span>
            {isCurrentWeek && (
              <div className="mt-1.5 flex items-center gap-1.5 sm:justify-end">
                <span className="text-gray-500 text-[10px] uppercase tracking-wider">Extra</span>
                <OrderStepper value={m.extra} onChange={(v) => saveExtra(it.id, v)} />
              </div>
            )}
          </div>
        </div>

        {isOpen && (
          <div className="mt-2.5 pt-2.5 border-t border-dashed border-gray-700 flex flex-wrap gap-x-6 gap-y-1 text-xs text-gray-400">
            <span>Display shelves <b className="text-gray-200 tabular-nums">{m.display}</b></span>
            <span>Storage <b className="text-gray-200 tabular-nums">{m.storage}</b></span>
            <span>= <b className="text-gray-200 tabular-nums">{m.bottles} bottles</b> <span className="text-gray-500">({fmtQty(Math.round(m.casesOnHand * 100) / 100)} cs)</span></span>
            {m.production != null && m.production < 3 && (
              <span className="text-amber-400">In production — low: <b className="tabular-nums">{fmtQty(m.production)}</b> cases left</span>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      {/* Week + completeness */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-4">
        <label className="flex items-center gap-2 text-sm text-gray-400">
          Week
          <select value={weekStart} onChange={e => onWeekChange(e.target.value)}
            className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-orange-500">
            {weekOptions.map(w => <option key={w} value={w}>{fmtWeekRange(w)}{w === currentWeek ? ' (current)' : ''}</option>)}
          </select>
        </label>
        {total > 0 && (
          <span className={`text-xs font-semibold px-2.5 py-1 rounded-full self-start sm:self-auto ${
            isComplete ? 'bg-green-500/15 text-green-400 border border-green-500/30'
                       : 'bg-amber-500/10 text-amber-400 border border-amber-500/30'}`}>
            {isComplete ? '✓ All counted' : `${counted} / ${total} counted`}
          </span>
        )}
      </div>

      {/* Summary + primary action */}
      <div className="bg-gradient-to-b from-gray-800 to-gray-800/60 border border-gray-700 rounded-2xl p-5 mb-5 flex items-center gap-5 flex-wrap">
        <div className="flex items-end gap-2">
          <span className="text-3xl font-bold tabular-nums text-orange-400 leading-none">{toOrder.length}</span>
          <span className="text-gray-400 text-xs uppercase tracking-wider mb-0.5">spirits to order</span>
        </div>
        <div className="w-px self-stretch bg-gray-700" />
        <div className="flex items-end gap-2">
          <span className="text-3xl font-bold tabular-nums text-white leading-none">{fmtQty(totalCases)}</span>
          <span className="text-gray-400 text-xs uppercase tracking-wider mb-0.5">cases total</span>
        </div>
        <div className="flex-1" />
        {isCurrentWeek ? (
          <button onClick={onShowSend}
            className="text-white text-sm font-semibold px-5 py-3 rounded-xl flex items-center gap-2 whitespace-nowrap"
            style={{ backgroundColor: '#F05A28' }}>
            🛒 Review &amp; Send Order
          </button>
        ) : (
          <span className="text-xs text-amber-400 bg-amber-500/10 border border-amber-500/30 rounded-full px-3 py-1.5">
            Viewing a past week — read-only
          </span>
        )}
      </div>

      {/* Filter + search */}
      <div className="flex items-center gap-3 mb-3 flex-wrap">
        <div className="inline-flex bg-gray-800 border border-gray-700 rounded-lg p-0.5">
          {[['toorder', `To order (${toOrder.length})`], ['all', `All (${items.length})`]].map(([v, label]) => (
            <button key={v} onClick={() => setFilter(v)}
              className={`text-sm font-semibold px-3.5 py-1.5 rounded-md transition ${filter === v ? 'text-white' : 'text-gray-400 hover:text-white'}`}
              style={filter === v ? { backgroundColor: '#F05A28' } : {}}>{label}</button>
          ))}
        </div>
        <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search spirits…"
          className="flex-1 min-w-[150px] max-w-xs bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm" />
      </div>

      {shown.length === 0 ? (
        <div className="bg-gray-800 border border-gray-700 rounded-xl py-12 text-center text-gray-500 text-sm">
          {items.length === 0
            ? 'No spirits yet. Sync the distillery inventory in Manage → Settings.'
            : filter === 'toorder' ? 'Nothing needs ordering this week. 🎉' : 'No spirits match that search.'}
        </div>
      ) : (
        <div className="flex flex-col gap-2">{shown.map(renderRow)}</div>
      )}

      {/* Collapsed "nothing needed" group, only in the To-order view */}
      {filter === 'toorder' && rest.length > 0 && (
        <>
          <button onClick={() => setRestOpen(o => !o)}
            className="flex items-center gap-2.5 w-full text-gray-400 text-xs font-semibold mt-5 mb-2 hover:text-gray-300">
            <span className="text-gray-500">{restOpen ? '▾' : '▸'}</span>
            Counted, nothing needed ({rest.length})
            <span className="flex-1 h-px bg-gray-700" />
          </button>
          {restOpen && <div className="flex flex-col gap-2">{rest.map(renderRow)}</div>}
        </>
      )}
    </>
  );
}

// ── Main component ──────────────────────────────────────────────────────────

export default function SpiritsOrdering({ user, canUpload, onBack, focus }) {
  const isAdmin = user?.roles?.includes('admin') || user?.role === 'admin';
  const [location, setLocation] = useState(null);
  const [tab, setTab] = useState('order');
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState([]);
  const [extras, setExtras] = useState([]);
  const [pars, setPars] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showSend, setShowSend] = useState(false);
  const [flash, setFlash] = useState('');
  const currentWeek = isoWeekStart();
  const [weekStart, setWeekStart] = useState(currentWeek);
  const isCurrentWeek = weekStart === currentWeek;

  // Last 12 ISO weeks (Mondays), newest first — the historical picker options.
  const weekOptions = useMemo(() => {
    const [y, m, d] = currentWeek.split('-').map(Number);
    const monday = new Date(y, m - 1, d);
    const opts = [];
    for (let i = 0; i < 12; i++) {
      const dt = new Date(monday);
      dt.setDate(dt.getDate() - i * 7);
      opts.push(dt.toLocaleDateString('en-CA'));
    }
    return opts;
  }, [currentWeek]);

  // Switching locations always drops back to the current week.
  useEffect(() => { setWeekStart(currentWeek); }, [location, currentWeek]);

  // Deep-link: /spirits-ordering?location=midtown&week=YYYY-MM-DD. Set the location
  // first; the week is applied in the effect BELOW this one (so it runs after the
  // location-reset effect above and isn't clobbered).
  const focusRef = useRef({ done: false, week: null });
  useEffect(() => {
    if (focusRef.current.done || !focus?.location) return;
    if (!LOCATIONS.some((l) => l.key === focus.location)) return;
    focusRef.current = { done: true, week: focus.week || null };
    setLocation(focus.location);
  }, [focus]);
  useEffect(() => {
    if (focusRef.current.week && location === focus?.location) {
      const w = focusRef.current.week;
      focusRef.current.week = null;
      setWeekStart(w);
    }
  }, [location, focus]);

  const loadItems = useCallback(async () => {
    const r = await fetch(`${API}/api/spirits/items${canUpload && tab === 'manage' ? '?includeHidden=1' : ''}`, { credentials: 'include' });
    if (r.ok) setItems(await r.json());
  }, [canUpload, tab]);

  const loadCounts = useCallback(async () => {
    if (!location) return;
    const r = await fetch(`${API}/api/spirits/counts?location=${location}&week_start=${weekStart}`, { credentials: 'include' });
    if (r.ok) {
      const d = await r.json();
      setCounts(d.counts || []);
      setExtras(d.extras || []);
    }
  }, [location, weekStart]);

  const loadPars = useCallback(async () => {
    if (!location) return;
    const r = await fetch(`${API}/api/spirits/pars?location=${location}`, { credentials: 'include' });
    if (r.ok) setPars(await r.json());
  }, [location]);

  const loadSettings = useCallback(async () => {
    const r = await fetch(`${API}/api/spirits/settings`, { credentials: 'include' });
    if (r.ok) setSettings(await r.json());
  }, []);

  useEffect(() => { loadSettings(); }, [loadSettings]);

  useEffect(() => {
    if (!location) return;
    setLoading(true);
    Promise.all([loadItems(), loadCounts(), loadPars()]).finally(() => setLoading(false));
  }, [location, loadItems, loadCounts, loadPars]);

  const handleCountSaved = (itemId, area, qty) => {
    const key = area === 'display' ? 'display_qty' : 'storage_qty';
    setCounts(prev => {
      const existing = prev.find(c => c.item_id === itemId);
      if (existing) return prev.map(c => c.item_id === itemId ? { ...c, [key]: qty } : c);
      return [...prev, { item_id: itemId, display_qty: 0, storage_qty: 0, [key]: qty, submitted_by_name: user.name }];
    });
  };

  const handleExtraSaved = (itemId, extra) => {
    setExtras(prev => {
      if (extra == null) return prev.filter(e => e.item_id !== itemId);
      const existing = prev.find(e => e.item_id === itemId);
      if (existing) return prev.map(e => e.item_id === itemId ? { ...e, extra_qty: extra } : e);
      return [...prev, { item_id: itemId, extra_qty: extra, set_by_name: user.name }];
    });
  };

  const handleParSaved = (itemId, parBottles) => {
    setPars(prev => {
      const existing = prev.find(p => p.item_id === itemId);
      if (existing) return prev.map(p => p.item_id === itemId ? { ...p, par_bottles: parBottles } : p);
      return [...prev, { item_id: itemId, par_bottles: parBottles }];
    });
  };

  const handleFlash = (msg) => {
    setFlash(msg);
    setTimeout(() => setFlash(''), 4000);
  };

  const locationLabel = LOCATIONS.find(l => l.key === location)?.label || '';

  if (!location) {
    return (
      <div className="min-h-screen bg-gray-900">
        <TopNav />
        <LocationLanding onSelect={setLocation} lastSync={settings?.last_sync_at} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900">
      <TopNav>
        <div className="flex items-center justify-between gap-3">
          <h1 className="text-cream font-bold text-base sm:text-xl truncate">{locationLabel} Spirits Order</h1>
          <div className="flex items-center gap-3 flex-shrink-0">
            {canUpload && (
              <button onClick={() => setTab(tab === 'manage' ? 'order' : 'manage')}
                className="text-sm text-gray-400 hover:text-white transition">
                {tab === 'manage' ? '← Order' : '⚙ Manage'}
              </button>
            )}
            <button onClick={() => { setLocation(null); setTab('order'); }}
              className="text-sm text-gray-400 hover:text-white transition">← Locations</button>
          </div>
        </div>
      </TopNav>

      <div className="px-4 sm:px-6 py-5 max-w-screen-xl mx-auto">
        {flash && (
          <div className="mb-4 px-4 py-3 rounded-lg bg-green-500/20 border border-green-500/40 text-green-300 text-sm">{flash}</div>
        )}

        {loading ? (
          <p className="text-gray-500 text-sm text-center py-12">Loading…</p>
        ) : (tab === 'manage' && canUpload) ? (
          <ManageTab
            location={location}
            locationLabel={locationLabel}
            items={items}
            pars={pars}
            settings={settings}
            isAdmin={isAdmin}
            onChange={() => { loadPars(); loadSettings(); }}
            onItemsChanged={() => { loadItems(); loadSettings(); }}
          />
        ) : canUpload ? (
          <OrderScreen
            location={location}
            locationLabel={locationLabel}
            items={items.filter(i => !i.hidden)}
            counts={counts}
            extras={extras}
            pars={pars}
            weekStart={weekStart}
            currentWeek={currentWeek}
            isCurrentWeek={isCurrentWeek}
            weekOptions={weekOptions}
            onWeekChange={setWeekStart}
            onExtraSaved={handleExtraSaved}
            onParSaved={handleParSaved}
            onShowSend={() => setShowSend(true)}
          />
        ) : (
          // View-only (no upload): read-only counts, no order/send.
          <>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-4">
              <label className="flex items-center gap-2 text-sm text-gray-400">
                Week
                <select value={weekStart} onChange={e => setWeekStart(e.target.value)}
                  className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-orange-500">
                  {weekOptions.map(w => (
                    <option key={w} value={w}>{fmtWeekRange(w)}{w === currentWeek ? ' (current)' : ''}</option>
                  ))}
                </select>
              </label>
            </div>
            <InventoryTab
              location={location}
              items={items.filter(i => !i.hidden)}
              counts={counts}
              extras={extras}
              pars={pars}
              weekStart={weekStart}
              canUpload={false}
              countsEditable={false}
              showDiscrepancy={false}
              extrasEditable={false}
              onCountSaved={handleCountSaved}
              onExtraSaved={handleExtraSaved}
            />
          </>
        )}
      </div>

      {showSend && (
        <SendOrderModal
          location={location}
          locationLabel={locationLabel}
          weekStart={currentWeek}
          onClose={() => setShowSend(false)}
          onSent={handleFlash}
        />
      )}
    </div>
  );
}
